import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, Mail, Ticket } from "lucide-react";

// Build QR code image URL (uses external QR API for inline browser rendering)
function qrSrc(token) {
    return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=4&data=${encodeURIComponent(token)}`;
}

export default function TicketsSuccess() {
    const [params] = useSearchParams();
    const session_id = params.get("session_id");
    const [status, setStatus] = useState("checking");
    const [tries, setTries] = useState(0);
    const [order, setOrder] = useState(null);

    useEffect(() => {
        if (!session_id) { setStatus("error"); return; }
        let cancelled = false;
        let attempts = 0;
        const poll = async () => {
            attempts++;
            setTries(attempts);
            try {
                const { data } = await api.get(`/payments/status/${session_id}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    // fetch the order with QR token
                    try {
                        const o = await api.get(`/tickets/by-session/${session_id}`);
                        setOrder(o.data);
                    } catch {}
                    setStatus("paid");
                } else if (data.status === "expired") setStatus("expired");
                else if (attempts >= 8) setStatus("timeout");
                else setTimeout(poll, 2000);
            } catch {
                if (attempts >= 8) setStatus("error"); else setTimeout(poll, 2000);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [session_id]);

    return (
        <div data-testid="tickets-success-page" className="min-h-[70vh] px-5 py-16 grain">
            <div className="max-w-2xl mx-auto">
                <div className="card-flat p-10 text-center">
                    {status === "checking" && (
                        <>
                            <Loader2 className="mx-auto text-gold animate-spin" size={40} />
                            <h2 className="h-display text-3xl mt-5">Confirming Payment…</h2>
                            <p className="text-ink mt-3 text-sm">Attempt {tries}/8</p>
                        </>
                    )}
                    {status === "paid" && (
                        <>
                            <CheckCircle2 className="mx-auto text-green-400" size={48} />
                            <h2 className="h-display text-4xl mt-5">Tickets Locked.</h2>
                            <p className="text-white/80 mt-3">
                                {order ? <>Charged <span className="text-gold">${order.total_amount.toFixed(2)}</span>. </> : null}
                                Receipt + QR sent to your email. See you at the arena.
                            </p>

                            {/* QR + order details */}
                            {order && order.qr_token && (
                                <div className="mt-8 bg-white p-6 inline-block" data-testid="ticket-qr">
                                    <img src={qrSrc(order.qr_token)} alt="QR ticket" width={240} height={240} />
                                    <div className="text-[10px] text-purple-dark font-mono mt-3 tracking-widest">{order.qr_token.slice(0, 24)}…</div>
                                </div>
                            )}

                            {order && (
                                <div className="mt-8 text-left border-t border-line pt-6 text-sm">
                                    <Row label="Order" value={order.id.slice(0, 8).toUpperCase()} />
                                    <Row label="Tier" value={order.tier_name} />
                                    <Row label="Quantity" value={order.quantity} />
                                    <Row label="Email" value={order.buyer_email} />
                                </div>
                            )}

                            <div className="flex gap-3 justify-center mt-7 flex-wrap">
                                <Link to="/schedule" className="btn-gold px-7 py-4 text-xs inline-flex items-center gap-2" data-testid="tk-schedule-btn">
                                    <Ticket size={14} /> View Schedule
                                </Link>
                                <Link to="/tickets/transfer" className="btn-ghost-gold px-7 py-4 text-xs" data-testid="tk-transfer-btn">Transfer Ticket</Link>
                                <Link to="/" className="btn-ghost-gold px-7 py-4 text-xs">Home</Link>
                            </div>

                            <div className="text-[10px] tracking-widest uppercase text-ink mt-6 flex items-center justify-center gap-2">
                                <Mail size={11} /> Check spam if you don't see the email
                            </div>
                        </>
                    )}
                    {(status === "expired" || status === "timeout" || status === "error") && (
                        <>
                            <XCircle className="mx-auto text-red-400" size={48} />
                            <h2 className="h-display text-3xl mt-5">Payment Not Confirmed</h2>
                            <p className="text-ink mt-3">{status === "expired" ? "Your session expired." : "Couldn't verify payment. Try again or contact support."}</p>
                            <Link to="/tickets" className="btn-ghost-gold inline-block mt-6 px-7 py-4 text-xs">Try Again</Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between py-1.5">
            <div className="text-[10px] tracking-widest uppercase text-ink">{label}</div>
            <div className="text-white">{value}</div>
        </div>
    );
}
